import React, { useState, useEffect } from 'react';
import { Tag, Plus, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Button that detects betting content in messages and allows adding to bet slip
 * @param {boolean} manualTrigger - If true, won't auto-detect, waits for showBox prop
 * @param {boolean} showBox - If true and manualTrigger is true, shows the box
 */
const BetDetectionButton = ({ message, isUser, manualTrigger = false, showBox = false }) => {
  const [betLines, setBetLines] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [addedBets, setAddedBets] = useState(new Set());

  useEffect(() => {
    // Auto-detect mode (default behavior)
    if (!manualTrigger && !isUser && message?.text) {
      console.log('BetDetectionButton - Auto-analyzing message:', message.text);
      detectBets(message.text);
    }
    // Manual trigger mode
    else if (manualTrigger && showBox && !isUser && message?.text) {
      console.log('BetDetectionButton - Manual trigger activated:', message.text);
      detectBets(message.text);
    }
    // Clear bets when box is hidden in manual mode
    else if (manualTrigger && !showBox) {
      setBetLines([]);
    }
  }, [message, isUser, manualTrigger, showBox]);

  const detectBets = (text) => {
    const detectedBets = [];
    const seenNormalized = new Set();
    
    // Extract the main betting recommendation first
    const bestBetMatch = text.match(/\*\*Best Bet\*\*:?\s*([^.\n]+)/i);
    if (bestBetMatch) {
      const betText = bestBetMatch[1].trim().replace(/^\*\*|\*\*$/g, '');
      const normalized = betText.toLowerCase().replace(/[^\w\s]/g, '').trim();
      seenNormalized.add(normalized);
      detectedBets.push(betText);
    }
    
    // Enhanced patterns for better detection
    const additionalPatterns = [
      // Team with spread - expanded team list for all sports
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+([+-]\d+\.?\d*)(?:\s+points?)?/g,
      
      // Moneyline/To Win patterns
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+to\s+win/gi,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+moneyline/gi,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*):?\s+([+-]?\d{3,4})(?:\s|$)/g, // Odds like Chiefs: -150
      
      // Over/Under with various formats
      /(Over|Under)\s+(\d+\.?\d*)(?:\s+(?:points?|pts?|total))?/gi,
      /(O|U)\s+(\d+\.?\d*)/g, // Short form O/U
      
      // Recommendation patterns
      /(?:consider|take|bet|play)\s+(?:the\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+([+-]\d+\.?\d*)/gi,
      /(?:consider|take|bet|play)\s+(?:the\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:against\s+the\s+spread|ATS)/gi,
      
      // Specific recommendations in your example
      /(Chargers)\s+(?:against\s+the\s+spread|ATS|\+\d+\.?\d*)/gi,
      /(Chiefs)\s+(?:are\s+solid\s+favorites|to\s+win|-\d+\.?\d*)/gi,
    ];
    
    additionalPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        let betText = match[0].trim();
        
        // Clean up the bet text
        betText = betText.replace(/^(consider|take|bet|play)\s+(?:the\s+)?/i, '');
        betText = betText.replace(/\s+(?:against\s+the\s+spread|ATS)$/i, ' ATS');
        
        const normalized = betText.toLowerCase().replace(/[^\w\s]/g, '').trim();
        
        // Check if this bet is already in our list (normalized comparison)
        let isDuplicate = false;
        for (const existing of seenNormalized) {
          if (existing.includes(normalized) || normalized.includes(existing)) {
            isDuplicate = true;
            break;
          }
        }
        
        if (!isDuplicate && betText.length > 3 && betText.length < 50) {
          seenNormalized.add(normalized);
          detectedBets.push(betText);
        }
      }
    });
    
    // Remove redundant entries
    const finalBets = detectedBets.filter((bet, index) => {
      return index === 0 || !detectedBets.slice(0, index).some(prevBet => 
        prevBet.toLowerCase().includes(bet.toLowerCase())
      );
    });
    
    console.log('Detected bets:', finalBets.slice(0, 5)); // Show up to 5 bets
    setBetLines(finalBets.slice(0, 5)); // Allow up to 5 bets
  };

  const handleAddToBetSlip = async (betText) => {
    if (window.addToBetSlip) {
      setIsAnalyzing(true);
      await window.addToBetSlip(betText);
      setAddedBets(prev => new Set([...prev, betText]));
      setIsAnalyzing(false);
    }
  };

  const handleAddAll = async () => {
    if (window.addToBetSlip) {
      setIsAnalyzing(true);
      for (const bet of betLines) {
        if (!addedBets.has(bet)) {
          await window.addToBetSlip(bet);
          setAddedBets(prev => new Set([...prev, bet]));
          // Small delay between adds to avoid overwhelming
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      setIsAnalyzing(false);
    }
  };

  // Don't show for user messages or if no bets detected
  if (isUser || betLines.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mt-2 p-2 bg-emerald-900/15 border border-emerald-700/20 rounded-lg"
      >
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-medium text-emerald-300">
              Quick Add to Pick Slip
            </span>
          </div>
          {betLines.length > 1 && (
            <button
              onClick={handleAddAll}
              disabled={isAnalyzing || betLines.every(bet => addedBets.has(bet))}
              className="px-2 py-0.5 text-xs bg-emerald-600/80 hover:bg-emerald-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center gap-1"
            >
              {isAnalyzing ? (
                <>
                  <Loader className="w-3 h-3 animate-spin" />
                  <span>Adding...</span>
                </>
              ) : betLines.every(bet => addedBets.has(bet)) ? (
                <span>Added</span>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  <span>Add All</span>
                </>
              )}
            </button>
          )}
        </div>
        
        <div className="space-y-1">
          {betLines.map((bet, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.03 }}
              className="flex items-center justify-between p-1.5 bg-slate-800/30 rounded"
            >
              <span className="text-xs text-white/80 flex-1 mr-2">{bet}</span>
              <button
                onClick={() => handleAddToBetSlip(bet)}
                disabled={addedBets.has(bet) || isAnalyzing}
                className={`px-2 py-0.5 text-xs rounded transition-all flex items-center gap-1 ${
                  addedBets.has(bet)
                    ? 'bg-emerald-700/30 text-emerald-400 cursor-default'
                    : 'bg-emerald-600/80 hover:bg-emerald-500 text-white'
                } disabled:cursor-not-allowed`}
              >
                {addedBets.has(bet) ? (
                  <span>✓</span>
                ) : (
                  <Plus className="w-3 h-3" />
                )}
              </button>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BetDetectionButton;